import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Phone, PackageSearch } from "lucide-react";
import OrderTimeline from "@/components/OrderTimeline";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BUSINESS } from "@/data/site";
import { WhatsAppIcon } from "@/components/Icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderPage() {
  const { orderId } = useParams();
  const { t } = useLanguage();
  usePageMeta(`Order ${orderId} | KAZIM'S`, t.orderPage.thanks);
  const [order, setOrder] = useState(undefined);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/orders/${orderId}`);
        const data = await res.json();
        setOrder(live && res.ok && data.ok ? data.order : null);
      } catch {
        if (live) setOrder(null);
      }
    })();
    return () => { live = false; };
  }, [orderId]);

  if (order === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-20">
        <span className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" aria-label="Loading" />
      </main>
    );
  }

  if (!order) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pb-20 pt-32">
        <div className="text-center">
          <PackageSearch className="mx-auto h-10 w-10 text-gold/60" strokeWidth={1.25} />
          <h1 className="mt-5 font-serif text-3xl text-cream">{t.orderPage.notFound}</h1>
          <Link to="/track" data-testid="order-notfound-track-link" className="mt-8 inline-block border border-gold/50 px-7 py-3.5 text-sm uppercase tracking-[0.15em] text-gold hover:bg-gold/10">
            {t.track.title}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pb-24 pt-32 md:pt-40">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <div className="text-center" data-testid="order-confirmation-header">
          <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.25} />
          <h1 className="mt-5 font-serif text-4xl text-cream sm:text-5xl">{t.orderPage.thanks}</h1>
          <p className="mt-4 text-[11px] font-medium uppercase tracking-luxe text-gold/90">{t.orderPage.orderLabel}</p>
          <p className="mt-1 font-serif text-2xl tracking-[0.2em] text-gold" data-testid="order-page-id">{order.order_id}</p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="border hairline bg-velvet/40 p-7" data-testid="order-status-card">
            <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">{t.orderPage.statusLabel}</h2>
            <div className="mt-6">
              <OrderTimeline order={order} />
            </div>
          </div>

          <div className="space-y-6">
            <div className="border hairline bg-velvet/40 p-7" data-testid="order-details-card">
              <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">{t.orderPage.items}</h2>
              {order.items.map((it, i) => (
                <div key={i} className="mt-4 flex items-start justify-between gap-4 text-sm">
                  <span className="text-cream/80">
                    {it.name}
                    <span className="block text-[12px] text-cream/45">{it.sku} · × {it.units}</span>
                  </span>
                  <span className="text-cream/80">₹{it.selling_price * it.units}</span>
                </div>
              ))}
              <div className="mt-5 flex items-center justify-between border-t hairline pt-4">
                <span className="text-sm text-cream/60">{t.orderPage.total}</span>
                <span className="font-serif text-2xl text-gold">₹{order.subtotal}</span>
              </div>
              <p className="mt-4 text-[12px] text-cream/55">
                {t.orderPage.payment}: {t.orderPage.paymentStatus[order.payment_status] || order.payment_status}
                {" · "}
                {order.payment_method}
              </p>
            </div>

            <div className="border hairline bg-velvet/40 p-7" data-testid="order-address-card">
              <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">{t.orderPage.deliverTo}</h2>
              <address className="mt-4 text-sm not-italic leading-relaxed text-cream/70">
                <span className="block text-cream">{order.customer.name}</span>
                {order.customer.address},<br />
                {order.customer.city}, {order.customer.state} - {order.customer.pincode},<br />
                India<br />
                {order.customer.phone}
                {order.customer.email ? <span className="block">{order.customer.email}</span> : null}
              </address>
            </div>
          </div>
        </div>

        <div className="mt-10 border hairline bg-velvet/40 p-7" data-testid="order-help-card">
          <h2 className="text-[11px] font-medium uppercase tracking-luxe text-gold/90">{t.orderPage.needHelp}</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={BUSINESS.tel} data-testid="order-help-call-link" className="inline-flex items-center gap-2.5 bg-gold px-6 py-3 text-sm font-medium uppercase tracking-[0.12em] text-obsidian transition-all duration-300 hover:bg-gold-light">
              <Phone className="h-4 w-4" strokeWidth={1.5} />
              {t.orderPage.call}
            </a>
            <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="order-help-whatsapp-link" className="inline-flex items-center gap-2.5 border border-gold/50 px-6 py-3 text-sm uppercase tracking-[0.12em] text-gold transition-all duration-300 hover:bg-gold/10">
              <WhatsAppIcon className="h-4 w-4" />
              {t.orderPage.whatsapp}
            </a>
            <Link to="/" data-testid="order-home-link" className="inline-flex items-center gap-2.5 border border-cream/25 px-6 py-3 text-sm uppercase tracking-[0.12em] text-cream/70 transition-all duration-300 hover:border-gold hover:text-gold">
              {t.orderPage.home}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
